import {
  FinancingInputs,
  FinancingSelector,
} from '@airship-platform/airship-proposal';
import {
  EmptyState,
  FlexBox,
  LoadingButton,
} from '@airship-platform/jam-web/components';
import { PlusIcon } from '@airship-platform/jam-web/icons';
import Button from '@mui/material/Button';
import { Link, useNavigate } from 'react-router';
import { useAppointmentProposalOptions } from '../../../data-access/appointmentProposalOptions';
import { useFinancingConfig } from '../../../data-access/financingConfig';
import { SectionWrapper } from '../../../patterns';

const FORM_ID = 'financingForm';

const FinancingPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    options,
    updateAppointmentProposalOption,
    isLoading: isOptionsLoading,
  } = useAppointmentProposalOptions();
  const { financingConfigs, isLoading: isFinancingConfigsLoading } =
    useFinancingConfig();

  if (!options || options.length === 0) {
    return (
      <EmptyState
        title="No options found"
        subtitle="Please add options to the appointment proposal."
      />
    );
  }

  if (!financingConfigs || financingConfigs.length === 0) {
    return (
      <EmptyState
        title="No financing configs found"
        subtitle="Please add financing configs to the appointment proposal."
      />
    );
  }

  const defaultFinancialPlan = financingConfigs
    .filter((config) => config.isDefault)
    .at(0);

  const defaultValues: FinancingInputs = {
    options: options.map((option) => ({
      appointmentId: option.appointmentId,
      appointmentProposalId: option.appointmentProposalId,
      appointmentProposalOptionId: option.appointmentProposalOptionId,
      financingId: option.optionFinancingDisabled
        ? null
        : option.financingPlanId || defaultFinancialPlan?.accountingId || null,
      optionFinancingDisabled: option.optionFinancingDisabled || false,
      availableFinancingPlanIds: option.availableFinancingPlanIds,
    })),
  };

  const onSubmit = (data: FinancingInputs) =>
    Promise.all(
      data.options.map((option) =>
        updateAppointmentProposalOption(
          option.appointmentId,
          option.appointmentProposalId,
          option.appointmentProposalOptionId,
          {
            financingPlanId: option.financingId || undefined,
            optionFinancingDisabled: option.optionFinancingDisabled,
            availableFinancingPlanIds: option.availableFinancingPlanIds,
          }
        )
      )
    ).then(() => navigate('../service-menu'));

  return (
    <SectionWrapper
      maxWidth={'48rem'}
      width={'100%'}
      header="Edit financing"
      subheader="Manage which plans are visible and set the default plan. The default determines the financing price displayed in the proposal."
      actionsButtons={{
        left: {
          color: 'softGold',
          component: Link,
          to: '../service-menu',
          label: 'Cancel',
        },
        right: {
          type: 'submit',
          component: LoadingButton,
          form: FORM_ID,
          label: 'Save',
          loading: isOptionsLoading || isFinancingConfigsLoading,
          disabled: isOptionsLoading || isFinancingConfigsLoading,
        },
      }}
    >
      <FlexBox flexDirection={'column'} gap={3} width={'100%'}>
        <FlexBox alignItems={'center'} gap={1}>
          <Button
            color="softGold"
            size="small"
            component={Link}
            startIcon={<PlusIcon />}
            to={'leasing-plan'}
          >
            Leasing plan
          </Button>
          <Button
            color="softGold"
            size="small"
            component={Link}
            startIcon={<PlusIcon />}
            to={'down-payment'}
          >
            Down payment
          </Button>
        </FlexBox>

        <FinancingSelector
          formId={FORM_ID}
          options={options}
          financingConfigs={financingConfigs}
          defaultValues={defaultValues}
          onSubmit={onSubmit}
        />
      </FlexBox>
    </SectionWrapper>
  );
};

export default FinancingPage;
